// src/pages/tenant/TenantDashboardPage.tsx
import React from 'react';
import {
  Box, Typography, Paper, Grid, Alert, Skeleton, Stack, Button,
  IconButton, Tooltip, Divider, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip,
  ResponsiveContainer, Legend, LineChart, Line,
} from 'recharts';
import api from '../../services/api';

import DashboardIcon     from '@mui/icons-material/Dashboard';
import RefreshIcon       from '@mui/icons-material/Refresh';
import BusinessIcon      from '@mui/icons-material/Business';
import ApartmentIcon     from '@mui/icons-material/Apartment';
import PeopleIcon        from '@mui/icons-material/People';
import DevicesIcon       from '@mui/icons-material/Devices';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import TrendingUpIcon    from '@mui/icons-material/TrendingUp';
import CheckCircleIcon   from '@mui/icons-material/CheckCircle';
import PaymentsIcon      from '@mui/icons-material/Payments';
import ReceiptLongIcon   from '@mui/icons-material/ReceiptLong';
import WarningAmberIcon  from '@mui/icons-material/WarningAmber';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';

// ─── Types ────────────────────────────────────────────────────────────────────
type TenantStats = {
  totalCompanies: number;
  activeCompanies: number;
  totalUsers: number;
  totalDevices: number;
  totalVehicles: number;
};

type BillingOverview = {
  totalCompanies: number;
  isConfigured: boolean;
  invoicesThisMonth: number;
  totalBilledThisMonth: number;
  totalPaidThisMonth: number;
  totalOutstanding: number;
  overdueCount: number;
  currency: string;
};

type AnalyticsMonth = {
  month: string;
  billed: number;
  collected: number;
  outstanding: number;
  overdueCount: number;
  invoiceCount: number;
};

type BillingAnalytics = {
  data: AnalyticsMonth[];
  totalBilled: number;
  totalCollected: number;
  totalOutstanding: number;
  collectionRate: number;
  period: string;
};

const fmt = (n: number, currency = 'NGN') => {
  try {
    if (n >= 1_000_000) return `${currency} ${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000)     return `${currency} ${(n / 1_000).toFixed(0)}K`;
    return new Intl.NumberFormat('en-NG', { style: 'currency', currency, maximumFractionDigits: 0 }).format(n);
  } catch {
    return `${currency} ${n.toFixed(0)}`;
  }
};

const fmtFull = (n: number, currency = 'NGN') => {
  try { return new Intl.NumberFormat('en-NG', { style: 'currency', currency }).format(n); }
  catch { return `${currency} ${n.toFixed(2)}`; }
};

// ─── Stat Card ────────────────────────────────────────────────────────────────
interface StatCardProps {
  label: string;
  value?: number | string;
  loading?: boolean;
  icon: React.ElementType;
  accent: string;
  bg: string;
  subtitle?: string;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, loading, icon: Icon, accent, bg, subtitle }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const border = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';

  return (
    <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: `1px solid ${border}`, background: isDark ? 'rgba(255,255,255,0.03)' : '#ffffff', transition: 'border-color 0.2s', '&:hover': { borderColor: accent } }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.45)', fontSize: '0.68rem' }}>
            {label}
          </Typography>
          {loading ? (
            <Skeleton variant="text" width={80} height={44} sx={{ mt: 0.5 }} />
          ) : (
            <Typography sx={{ fontSize: '1.8rem', fontWeight: 900, lineHeight: 1.15, mt: 0.5, color: isDark ? '#ffffff' : '#0f172a' }}>
              {typeof value === 'number' ? value.toLocaleString() : (value ?? '—')}
            </Typography>
          )}
          {subtitle && !loading && (
            <Typography sx={{ fontSize: '0.7rem', color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)', mt: 0.25 }}>{subtitle}</Typography>
          )}
        </Box>
        <Box sx={{ width: 44, height: 44, borderRadius: 2, display: 'grid', placeItems: 'center', background: bg, flexShrink: 0 }}>
          <Icon sx={{ color: accent, fontSize: 22 }} />
        </Box>
      </Stack>
    </Paper>
  );
};

// ─── Custom Tooltip for charts ────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label, currency }: any) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  if (!active || !payload?.length) return null;
  return (
    <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)'}`, background: isDark ? '#1e1e1e' : '#fff', minWidth: 160 }}>
      <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, color: isDark ? '#fff' : '#0f172a', mb: 0.75 }}>{label}</Typography>
      {payload.map((p: any) => (
        <Stack key={p.dataKey} direction="row" justifyContent="space-between" spacing={2}>
          <Typography sx={{ fontSize: '0.7rem', color: p.color, fontWeight: 700 }}>{p.name}</Typography>
          <Typography sx={{ fontSize: '0.7rem', color: isDark ? '#fff' : '#0f172a', fontWeight: 800 }}>{fmtFull(p.value, currency)}</Typography>
        </Stack>
      ))}
    </Paper>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const TenantDashboardPage: React.FC = () => {
  const theme    = useTheme();
  const isDark   = theme.palette.mode === 'dark';
  const [period, setPeriod] = React.useState<'monthly' | 'quarterly'>('monthly');

  const border       = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
  const surface      = isDark ? 'rgba(255,255,255,0.03)' : '#ffffff';
  const subtext      = isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)';
  const headingColor = isDark ? '#ffffff' : '#0f172a';
  const gridColor    = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';

  const { data: stats, isLoading: statsLoading, isError: statsError, refetch: refetchStats, isFetching: statsFetching } = useQuery({
    queryKey: ['tenant-admin', 'stats'],
    queryFn: async () => {
      const res = await api.get('/tenant-admin/stats');
      const d = res.data?.data ?? res.data;
      return {
        totalCompanies:  d.totalCompanies  ?? 0,
        activeCompanies: d.activeCompanies ?? 0,
        totalUsers:      d.totalUsers      ?? 0,
        totalDevices:    d.totalDevices    ?? 0,
        totalVehicles:   d.totalVehicles   ?? 0,
      } as TenantStats;
    },
    staleTime: 30_000,
    retry: 1,
  });

  const { data: billingOverview, isLoading: billingLoading, refetch: refetchBilling } = useQuery({
    queryKey: ['tenant-admin', 'billing-overview'],
    queryFn: async () => {
      const res = await api.get('/tenant-admin/billing/overview');
      return res.data?.data as BillingOverview;
    },
    staleTime: 60_000,
    retry: 1,
  });

  const { data: analytics, isLoading: analyticsLoading, refetch: refetchAnalytics } = useQuery({
    queryKey: ['tenant-admin', 'billing-analytics', period],
    queryFn: async () => {
      const res = await api.get('/tenant-admin/billing/analytics', { params: { period } });
      return res.data?.data as BillingAnalytics;
    },
    staleTime: 60_000,
    retry: 1,
  });

  const refetchAll = () => { refetchStats(); refetchBilling(); refetchAnalytics(); };
  const currency = billingOverview?.currency || analytics?.data?.[0] ? 'NGN' : 'NGN';

  const platformCards = [
    { label: 'Total Companies',  value: stats?.totalCompanies,  icon: BusinessIcon,      accent: '#38bdf8', bg: 'rgba(56,189,248,0.10)'  },
    { label: 'Active Companies', value: stats?.activeCompanies, icon: ApartmentIcon,     accent: '#34d399', bg: 'rgba(52,211,153,0.10)'  },
    { label: 'Total Users',      value: stats?.totalUsers,      icon: PeopleIcon,        accent: '#a78bfa', bg: 'rgba(167,139,250,0.10)' },
    { label: 'Total Devices',    value: stats?.totalDevices,    icon: DevicesIcon,       accent: '#fbbf24', bg: 'rgba(251,191,36,0.10)'  },
    { label: 'Total Vehicles',   value: stats?.totalVehicles,   icon: DirectionsCarIcon, accent: '#f87171', bg: 'rgba(248,113,113,0.10)' },
  ];

  const billingCards = [
    { label: 'Billed This Month',  value: fmt(billingOverview?.totalBilledThisMonth ?? 0, currency),   icon: ReceiptLongIcon,      accent: '#38bdf8', bg: 'rgba(56,189,248,0.10)',  subtitle: `${billingOverview?.invoicesThisMonth ?? 0} invoices` },
    { label: 'Collected',          value: fmt(billingOverview?.totalPaidThisMonth   ?? 0, currency),   icon: PaymentsIcon,         accent: '#34d399', bg: 'rgba(52,211,153,0.10)'  },
    { label: 'Outstanding',        value: fmt(billingOverview?.totalOutstanding     ?? 0, currency),   icon: AccountBalanceIcon,   accent: '#fbbf24', bg: 'rgba(251,191,36,0.10)'  },
    { label: 'Overdue Invoices',   value: billingOverview?.overdueCount ?? 0,                          icon: WarningAmberIcon,     accent: '#f87171', bg: 'rgba(248,113,113,0.10)', subtitle: 'require action' },
    { label: 'Collection Rate',    value: analytics ? `${analytics.collectionRate}%` : '—',            icon: TrendingUpIcon,       accent: '#a78bfa', bg: 'rgba(167,139,250,0.10)' },
  ];

  return (
    <Box>
      {/* ── Header ── */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box sx={{ width: 36, height: 36, borderRadius: 2, background: 'rgba(56,189,248,0.12)', display: 'grid', placeItems: 'center' }}>
            <DashboardIcon sx={{ color: '#38bdf8', fontSize: 20 }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 900, lineHeight: 1.2, color: headingColor }}>Platform Overview</Typography>
            <Typography variant="body2" sx={{ color: subtext, fontWeight: 500 }}>Totals and billing analytics across all companies</Typography>
          </Box>
        </Stack>
        <Tooltip title="Refresh all">
          <span>
            <IconButton onClick={refetchAll} disabled={statsFetching} size="small" sx={{ border: `1px solid ${border}`, borderRadius: 1.5, color: subtext }}>
              <RefreshIcon fontSize="small" sx={{ animation: statsFetching ? 'spin 1s linear infinite' : 'none' }} />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      {statsError && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2, background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.25)', color: '#f87171', '& .MuiAlert-icon': { color: '#f87171' } }}>
          Failed to load platform stats.
        </Alert>
      )}

      {/* ── Platform KPIs ── */}
      <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, color: subtext, textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1.5 }}>Platform</Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {platformCards.map(card => (
          <Grid item xs={12} sm={6} md={4} lg key={card.label}>
            <StatCard label={card.label} value={card.value} loading={statsLoading} icon={card.icon} accent={card.accent} bg={card.bg} />
          </Grid>
        ))}
      </Grid>

      {/* ── Billing KPIs ── */}
      {billingOverview?.isConfigured !== false && (
        <>
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, color: subtext, textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1.5 }}>Billing — This Month</Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {billingCards.map(card => (
              <Grid item xs={12} sm={6} md={4} lg key={card.label}>
                <StatCard label={card.label} value={card.value} loading={billingLoading} icon={card.icon} accent={card.accent} bg={card.bg} subtitle={card.subtitle} />
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {/* ── Revenue chart ── */}
      <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: `1px solid ${border}`, background: surface, mb: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <TrendingUpIcon sx={{ color: '#38bdf8', fontSize: 18 }} />
            <Typography sx={{ fontWeight: 900, fontSize: '0.85rem', color: headingColor }}>Revenue Trend</Typography>
          </Stack>
          <ToggleButtonGroup
            value={period}
            exclusive
            size="small"
            onChange={(_, v) => v && setPeriod(v)}
            sx={{ '& .MuiToggleButton-root': { px: 1.5, py: 0.4, fontSize: '0.72rem', fontWeight: 800, textTransform: 'none', border: `1px solid ${border}`, color: subtext, '&.Mui-selected': { background: '#38bdf8', color: '#000', borderColor: '#38bdf8' } } }}
          >
            <ToggleButton value="monthly">6 Months</ToggleButton>
            <ToggleButton value="quarterly">12 Months</ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        {analyticsLoading ? (
          <Skeleton height={220} sx={{ borderRadius: 2 }} />
        ) : analytics?.data?.length ? (
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={analytics.data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
              <XAxis dataKey="month" tick={{ fill: subtext, fontSize: 11, fontWeight: 700 }} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={(v) => fmt(v, currency)} tick={{ fill: subtext, fontSize: 10 }} axisLine={false} tickLine={false} width={72} />
              <ReTooltip content={<CustomTooltip currency={currency} />} />
              <Legend wrapperStyle={{ fontSize: '0.72rem', fontWeight: 700, color: subtext }} />
              <Bar dataKey="billed"      name="Billed"      fill="#38bdf8" radius={[3, 3, 0, 0]} maxBarSize={40} />
              <Bar dataKey="collected"   name="Collected"   fill="#34d399" radius={[3, 3, 0, 0]} maxBarSize={40} />
              <Bar dataKey="outstanding" name="Outstanding" fill="#fbbf24" radius={[3, 3, 0, 0]} maxBarSize={40} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <ReceiptLongIcon sx={{ fontSize: 32, color: subtext, mb: 1 }} />
            <Typography sx={{ fontWeight: 700, color: subtext, fontSize: '0.85rem' }}>No billing data yet</Typography>
            <Typography sx={{ fontSize: '0.75rem', color: subtext, mt: 0.5 }}>Configure billing and generate invoices to see revenue trends</Typography>
          </Box>
        )}
      </Paper>

      {/* ── Collection rate trend (line chart) ── */}
      {analytics?.data?.length ? (
        <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: `1px solid ${border}`, background: surface, mb: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <CheckCircleIcon sx={{ color: '#34d399', fontSize: 18 }} />
            <Typography sx={{ fontWeight: 900, fontSize: '0.85rem', color: headingColor }}>Invoice Count Trend</Typography>
          </Stack>
          <ResponsiveContainer width="100%" height={160}>
            <LineChart data={analytics.data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
              <XAxis dataKey="month" tick={{ fill: subtext, fontSize: 11, fontWeight: 700 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: subtext, fontSize: 10 }} axisLine={false} tickLine={false} width={30} />
              <ReTooltip content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null;
                return (
                  <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, border: `1px solid ${border}`, background: isDark ? '#1e1e1e' : '#fff' }}>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: headingColor, mb: 0.5 }}>{label}</Typography>
                    <Typography sx={{ fontSize: '0.7rem', color: '#a78bfa' }}>Invoices: <strong>{payload[0]?.value}</strong></Typography>
                    {payload[1] && <Typography sx={{ fontSize: '0.7rem', color: '#f87171' }}>Overdue: <strong>{payload[1]?.value}</strong></Typography>}
                  </Paper>
                );
              }} />
              <Line type="monotone" dataKey="invoiceCount" name="Invoices" stroke="#a78bfa" strokeWidth={2} dot={{ fill: '#a78bfa', r: 3 }} />
              <Line type="monotone" dataKey="overdueCount" name="Overdue"   stroke="#f87171" strokeWidth={2} dot={{ fill: '#f87171', r: 3 }} strokeDasharray="4 2" />
            </LineChart>
          </ResponsiveContainer>
        </Paper>
      ) : null}

      {/* ── Platform insights ── */}
      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: `1px solid ${border}`, background: surface }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
          <TrendingUpIcon sx={{ color: '#38bdf8', fontSize: 18 }} />
          <Typography sx={{ fontWeight: 900, fontSize: '0.8rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: subtext }}>Platform Insights</Typography>
        </Stack>
        <Divider sx={{ borderColor: border, mb: 2 }} />
        {statsLoading ? (
          <Stack spacing={1}>{[1, 2, 3].map(i => <Skeleton key={i} height={20} />)}</Stack>
        ) : (
          <Grid container spacing={2}>
            {[
              { label: 'Active rate',         value: stats?.totalCompanies ? `${Math.round(((stats.activeCompanies ?? 0) / stats.totalCompanies) * 100)}%` : '—',    icon: CheckCircleIcon, color: '#34d399' },
              { label: 'Users per company',   value: stats?.totalCompanies ? ((stats.totalUsers ?? 0) / stats.totalCompanies).toFixed(1) : '—',                       icon: PeopleIcon,      color: '#a78bfa' },
              { label: 'Devices per company', value: stats?.totalCompanies ? ((stats.totalDevices ?? 0) / stats.totalCompanies).toFixed(1) : '—',                     icon: DevicesIcon,     color: '#fbbf24' },
              ...(analytics ? [{ label: 'Collection rate', value: `${analytics.collectionRate}%`, icon: PaymentsIcon, color: '#34d399' }] : []),
            ].map(item => (
              <Grid item xs={12} sm={6} md={3} key={item.label}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box sx={{ width: 36, height: 36, borderRadius: 1.5, background: alpha(item.color, 0.1), display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                    <item.icon sx={{ color: item.color, fontSize: 18 }} />
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: subtext, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.65rem' }}>{item.label}</Typography>
                    <Typography sx={{ fontWeight: 900, fontSize: '1.1rem', color: headingColor, lineHeight: 1.2 }}>{item.value}</Typography>
                  </Box>
                </Stack>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </Box>
  );
};

export default TenantDashboardPage;